/**
 * useMaintenanceAlertSettings - Hook สำหรับจัดการ settings ของ Maintenance Alert System
 *
 * จัดเก็บและโหลด settings จาก localStorage
 * ใช้ร่วมกับ MaintenanceAlertContext
 */

import { useState, useEffect, useCallback } from 'react';

// =============== Types ===============

export interface MaintenanceAlertSettings {
  // Connection
  autoConnect: boolean;

  // Sound & Visual
  soundEnabled: boolean;
  tabFlashEnabled: boolean;
  popupEnabled: boolean;
  popupDuration: number; // milliseconds (3000-30000)

  // Priority Filter
  priorities: {
    urgent: boolean;
    high: boolean;
    normal: boolean;
    low: boolean;
  };
}

// =============== Constants ===============

const STORAGE_KEY = 'maintenance_alert_settings';

// S1133: cross-instance sync — different React components/contexts that consume this hook
// each hold their own state copy. We dispatch a custom event whenever the hook saves so all
// instances re-read localStorage and stay in sync within the same tab.
// (The native `storage` event only fires across tabs, not within the same tab.)
const SETTINGS_SYNC_EVENT = 'maintenance-alert-settings-changed';

const defaultSettings: MaintenanceAlertSettings = {
  autoConnect: true,
  soundEnabled: true,
  tabFlashEnabled: true,
  popupEnabled: true,
  popupDuration: 10000, // 10 seconds
  priorities: {
    urgent: true,
    high: true,
    normal: true,
    low: false,
  },
};

// =============== Hook ===============

export interface UseMaintenanceAlertSettingsReturn {
  settings: MaintenanceAlertSettings;
  updateSettings: (partial: Partial<MaintenanceAlertSettings>) => void;
  updatePriority: (priority: keyof MaintenanceAlertSettings['priorities'], enabled: boolean) => void;
  resetToDefaults: () => void;
  isLoaded: boolean;
}

export const useMaintenanceAlertSettings = (): UseMaintenanceAlertSettingsReturn => {
  const [settings, setSettings] = useState<MaintenanceAlertSettings>(defaultSettings);
  const [isLoaded, setIsLoaded] = useState(false);

  // Helper to load from localStorage (used by mount + cross-instance sync listeners).
  // Skips setSettings when value is deeply equal — prevents an infinite save→event→reload
  // cycle when this instance is the one that just wrote (its own save effect dispatches the
  // sync event which it then receives).
  const loadFromStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const next: MaintenanceAlertSettings = saved
        ? {
            ...defaultSettings,
            ...JSON.parse(saved),
            priorities: {
              ...defaultSettings.priorities,
              ...(JSON.parse(saved).priorities || {}),
            },
          }
        : defaultSettings;
      setSettings(prev =>
        JSON.stringify(prev) === JSON.stringify(next) ? prev : next
      );
    } catch (error) {
      console.warn('Failed to load maintenance alert settings:', error);
    }
  }, []);

  // Load settings from localStorage on mount + subscribe to cross-instance sync
  useEffect(() => {
    loadFromStorage();
    setIsLoaded(true);

    // S1133: keep multiple hook instances (WebSocketContext + MaintenanceAlertContext +
    // NotificationSettingsPage) in sync. The component that calls `updateSettings` writes
    // to localStorage then dispatches this event; all other instances re-read.
    const onSync = () => loadFromStorage();
    const onStorageEvent = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) loadFromStorage();
    };
    window.addEventListener(SETTINGS_SYNC_EVENT, onSync);
    window.addEventListener('storage', onStorageEvent);
    return () => {
      window.removeEventListener(SETTINGS_SYNC_EVENT, onSync);
      window.removeEventListener('storage', onStorageEvent);
    };
  }, [loadFromStorage]);

  // Save settings to localStorage when changed + broadcast for cross-instance sync
  useEffect(() => {
    if (isLoaded) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
        // S1133: notify peer hook instances (same tab) that settings changed
        window.dispatchEvent(new Event(SETTINGS_SYNC_EVENT));
      } catch (error) {
        console.warn('Failed to save maintenance alert settings:', error);
      }
    }
  }, [settings, isLoaded]);

  // Update partial settings
  const updateSettings = useCallback((partial: Partial<MaintenanceAlertSettings>) => {
    setSettings(prev => ({
      ...prev,
      ...partial,
    }));
  }, []);

  // Update single priority
  const updatePriority = useCallback(
    (priority: keyof MaintenanceAlertSettings['priorities'], enabled: boolean) => {
      setSettings(prev => ({
        ...prev,
        priorities: {
          ...prev.priorities,
          [priority]: enabled,
        },
      }));
    },
    []
  );

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setSettings(defaultSettings);
  }, []);

  return {
    settings,
    updateSettings,
    updatePriority,
    resetToDefaults,
    isLoaded,
  };
};

// =============== Utility Functions ===============

/**
 * ตรวจสอบว่า priority นี้ควรแจ้งเตือนหรือไม่ ตาม settings
 */
export const shouldNotifyForPriority = (
  priority: 'low' | 'normal' | 'high' | 'urgent',
  settings: MaintenanceAlertSettings
): boolean => {
  return settings.priorities[priority] ?? true;
};

/**
 * แปลง priority เป็นภาษาไทย
 */
export const getPriorityLabel = (priority: 'low' | 'normal' | 'high' | 'urgent'): string => {
  const labels = {
    urgent: 'เร่งด่วน',
    high: 'สำคัญ',
    normal: 'ปกติ',
    low: 'ต่ำ',
  };
  return labels[priority] || priority;
};

/**
 * แปลง priority เป็นสี
 */
export const getPriorityColor = (priority: 'low' | 'normal' | 'high' | 'urgent'): string => {
  const colors = {
    urgent: 'text-red-600 bg-red-100',
    high: 'text-orange-600 bg-orange-100',
    normal: 'text-gray-600 bg-gray-100',
    low: 'text-blue-600 bg-blue-100',
  };
  return colors[priority] || 'text-gray-600 bg-gray-100';
};

/**
 * แปลง priority เป็น badge color class
 */
export const getPriorityBadgeColor = (priority: 'low' | 'normal' | 'high' | 'urgent'): string => {
  const colors = {
    urgent: 'bg-red-500',
    high: 'bg-orange-500',
    normal: 'bg-gray-400',
    low: 'bg-blue-400',
  };
  return colors[priority] || 'bg-gray-400';
};

export default useMaintenanceAlertSettings;
